import { Injectable } from '@angular/core';
import { LinkTypes } from '@modules/card/enums/link-types.enum';
import { TargetType } from '@modules/card/enums/target-type.enum';
import { CardInterface } from '@modules/card/interfaces/card.interface';
import { FavoriteDataContextInterface } from '@modules/favorites/interfaces/favorite-data-context.interface';
import { LocalizedDatePipe } from '@modules/pipes/localized-date.pipe';
import { ActionIcons } from '@modules/ui/action-container/action-container.component';
import { TranslateService } from '@ngx-translate/core';

import { MediaSetting } from '../domain/media-setting.interface';

import { MediaAdapter } from './media.adapter';

export interface MagazineCardResponse {
  id: string;
  type: string;
  media: any;
  article_count: null | number;
  presskit?: any;
  tag_label: string;
  link?: {
    uri: string;
    title: string;
    options: any[];
  };
  relationships: {
    article: {
      id: string;
    };
    presskit: {
      id: string;
    };
    page: {
      id: string;
    };
  };
  fuel_label_text?: { processed: string };
  link_target: string;
  location: string | null;
  display_date?: string;
  slug: { lang: string; value: string }[];
  image_count: number;
  audio_count: number;
  document_count: number;
  technical_data_count: number;
  video_count: number;
  title: string;
  subtitle: string;
  mars_origin_id?: string;
  mars_shelf_number?: string;
}
@Injectable({
  providedIn: 'root',
})
export class MagazineCardAdapter {
  constructor(
    private mediaAdapter: MediaAdapter,
    private translateService: TranslateService,
    private localizedDatePipe: LocalizedDatePipe
  ) {}

  public parse(
    data: MagazineCardResponse,
    mediaSetting: MediaSetting[],
    context: FavoriteDataContextInterface
  ): CardInterface {
    let topline = data.location || '';
    if (data.location && data.display_date) {
      topline += ', ';
    }
    if (data.display_date) {
      topline += this.localizedDatePipe.transform(data.display_date, 'longDate');
    }
    const cardData: CardInterface = {
      text: data.subtitle,
      topline: topline,
      headline: data.title,
      fuelLabel: data?.fuel_label_text?.processed || data?.presskit?.fuel_label_text?.processed || '',
      media: data.media ? this.mediaAdapter.parse(data.media, mediaSetting, context) : undefined,
      trackingData: undefined,
    };
    const currentLang = this.translateService.currentLang.split('-')[0];
    let slug = '';
    if (data.slug) {
      slug = (data.slug?.find(({ lang }) => lang === currentLang) || data.slug[0])?.value.replace('page/', '') || '';
    }
    switch (data.type) {
      case 'link_card':
        cardData.link = {
          url: data.link?.uri || '',
          target: (data.link_target as TargetType) ?? TargetType.SELF,
          type: LinkTypes.EXTERNAL,
          query: '',
        };
        cardData.shareData = {
          title: data.title,
          content: data.subtitle,
          url: data.link?.uri || '',
        };
        cardData.trackingData = undefined; // nothing to track here
        cardData.actions = [{ icon: ActionIcons.SHARE }];
        break;
      case 'page_card':
        cardData.shareData = {
          title: data.title,
          content: data.subtitle,
          url: location.origin + slug.replace('/home', ''),
        };
        cardData.trackingData = {
          id: data.relationships?.page?.id,
          type: 'media_special',
          title: data.title,
          marsOriginId: data.mars_origin_id,
          shelfNumber: data.mars_shelf_number,
        };
        cardData.actions = [{ icon: ActionIcons.SHARE }];
        break;
      case 'article_card':
        slug = ''; // unset slug, so it will not be used as link
        const articleUrl = `/article/${data.relationships?.article?.id}`;
        cardData.link = {
          url: articleUrl,
          target: TargetType.SELF,
          type: LinkTypes.ROUTER,
          query: '',
        };
        cardData.favoriteData = {
          id: data.relationships?.article?.id,
          type: 'article',
        };
        cardData.shareData = {
          title: data.title,
          content: data.subtitle,
          url: location.origin + articleUrl,
        };
        cardData.downloadData = {
          id: data.relationships?.article?.id,
          type: 'article',
          fileType: 'article',
          format: 'ZIP',
          title: data.title,
          assets: {
            imageCount: data.image_count,
            videoCount: data.video_count,
            audioCount: data.audio_count,
            documentCount: data.document_count,
            technicalDataCount: data.technical_data_count,
          },
        };
        cardData.trackingData = {
          id: data.relationships?.article?.id,
          type: 'press_information',
          title: data.title,
          marsOriginId: data.mars_origin_id,
          shelfNumber: data.mars_shelf_number,
        };

        cardData.actions = [
          { icon: ActionIcons.SHARE },
          { icon: ActionIcons.DOWNLOAD },
          { icon: ActionIcons.BOOKMARK },
        ];
        break;

      case 'presskit_card':
        const pressKitUrl = `/press-kit/${data.relationships?.presskit?.id}`;
        cardData.type = data.type;
        cardData.link = {
          url: pressKitUrl,
          target: TargetType.SELF,
          type: LinkTypes.ROUTER,
          query: '',
        };
        cardData.trackingData = {
          id: data.relationships?.presskit?.id,
          type: 'press_kit',
          title: data.title,
          marsOriginId: data.mars_origin_id,
          shelfNumber: data.mars_shelf_number,
        };
        cardData.favoriteData = {
          id: data.relationships?.presskit?.id,
          type: 'presskit',
        };
        cardData.shareData = {
          title: data.title,
          content: data.subtitle,
          url: location.origin + pressKitUrl,
        };
        cardData.downloadData = {
          id: data.relationships?.presskit?.id,
          type: 'presskit',
          fileType: 'presskit',
          format: 'ZIP',
          title: data.title,
          assets: {
            imageCount: data.image_count,
            videoCount: data.video_count,
            audioCount: data.audio_count,
            documentCount: data.document_count,
            technicalDataCount: data.technical_data_count,
          },
        };
        cardData.actions = [
          { icon: ActionIcons.SHARE },
          { icon: ActionIcons.DOWNLOAD },
          { icon: ActionIcons.BOOKMARK },
        ];
        break;

      case 'media_card':
        if (data.media) {
          cardData.trackingData = {
            id: data.media.id,
            type: data.media.type,
            title: data.title,
            marsOriginId: data.mars_origin_id,
            shelfNumber: data.mars_shelf_number,
          };
          cardData.shareData = {
            title: data.title,
            content: data.subtitle,
            url: `${location.origin}${slug}/(lightbox:${data.media.type}/${data.media.id})`,
          };
          cardData.downloadData = {
            id: data.media.id,
            type: 'media',
            fileType: data.media.type,
            format: data.media.video ? (data.media.video?.[0]?.filemime.split('/').pop() as string) : 'JPG',
            title: data.title,
          };
          cardData.favoriteData = {
            id: data.media.id,
            type: data.media.type,
            context,
          };
          cardData.actions = [
            { icon: ActionIcons.SHARE },
            { icon: ActionIcons.DOWNLOAD },
            { icon: ActionIcons.BOOKMARK },
          ];
        }
        break;
    }

    const translatableMetaItems: any = [];
    if (slug) {
      cardData.link = {
        url: slug,
        target: TargetType.SELF,
        type: LinkTypes.ROUTER,
        query: '',
      };
    }
    if (data.article_count) {
      data.type === 'presskit_card'
        ? translatableMetaItems.push({
            key: data.article_count > 1 ? 'ARTICLES_N' : 'ARTICLE_N',
            data: {
              count: data.article_count,
            },
          })
        : translatableMetaItems.push({
            key: data.article_count > 1 ? 'CONTENTS_N' : 'CONTENT_N',
            data: {
              count: data.article_count,
            },
          });
    }
    if (data.image_count) {
      translatableMetaItems.push({
        key: data.image_count > 1 ? 'IMAGES_N' : 'IMAGE_N',
        data: {
          count: data.image_count,
        },
      });
    }
    if (data.video_count) {
      translatableMetaItems.push({
        key: data.video_count > 1 ? 'VIDEOS_N' : 'VIDEO_N',
        data: {
          count: data.video_count,
        },
      });
    }
    if (data.document_count) {
      translatableMetaItems.push({
        key: data.document_count > 1 ? 'DOCUMENTS_N' : 'DOCUMENT_N',
        data: {
          count: data.document_count,
        },
      });
    }
    if (data.technical_data_count) {
      translatableMetaItems.push({
        key: data.technical_data_count > 1 ? 'TECHNICAL_DATAS_N' : 'TECHNICAL_DATA_N',
        data: {
          count: data.technical_data_count,
        },
      });
    }
    if (data.audio_count) {
      translatableMetaItems.push({
        key: data.audio_count > 1 ? 'AUDIOS_N' : 'AUDIO_N',
        data: {
          count: data.audio_count,
        },
      });
    }

    if (translatableMetaItems.length) {
      cardData.translatableMetaItems = translatableMetaItems;
    }
    if (data.tag_label) {
      cardData.tags = [data.tag_label];
    }
    return cardData;
  }
}
